import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { format } from 'date-fns';
import { homeworkApi, classesApi, subjectsApi, studentsApi } from '../../services/api';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  BookOpenCheck,
  Plus,
  Send,
  Pencil,
  Trash2,
  Loader2,
  Share2,
  CalendarDays,
  MessageCircle,
  MessageSquare,
  CalendarIcon,
} from 'lucide-react';

const emptyForm = {
  class_id: '',
  subject_id: '',
  title: '',
  description: '',
  due_date: '',
};

export default function HomeworkPage() {
  const { user } = useAuth();
  const isTeacherOrAdmin = ['admin', 'teacher'].includes(user?.role);

  const [homework, setHomework] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filterClass, setFilterClass] = useState('all');

  // ----- Review (per-student comments) state -----
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewHw, setReviewHw] = useState(null);
  const [reviewStudents, setReviewStudents] = useState([]);
  // {student_id: {comment, dirty, saving}}
  const [reviewMap, setReviewMap] = useState({});
  const [reviewLoading, setReviewLoading] = useState(false);

  const openReview = async (hw) => {
    setReviewHw(hw);
    setReviewOpen(true);
    setReviewLoading(true);
    try {
      const [studentsRes, reviewsRes] = await Promise.all([
        studentsApi.getAll({ class_id: hw.class_id, limit: 100 }),
        api.get(`/homework/${hw.id}/reviews`),
      ]);
      const sts = studentsRes.data?.students || [];
      if (sts.length === 0) {
        toast.warning('No students found for this class. Add students first.');
      }
      const existing = {};
      (reviewsRes.data?.reviews || []).forEach((r) => {
        existing[r.student_id] = { comment: r.comment || '', dirty: false, saving: false };
      });
      sts.forEach((s) => {
        if (!existing[s.id]) existing[s.id] = { comment: '', dirty: false, saving: false };
      });
      setReviewStudents(sts);
      setReviewMap(existing);
    } catch (e) {
      console.error('openReview failed', e);
      toast.error(e.response?.data?.detail || `Failed to load review data (${e.response?.status || 'network error'})`);
    } finally {
      setReviewLoading(false);
    }
  };

  const closeReview = () => {
    setReviewOpen(false);
    setReviewHw(null);
    setReviewStudents([]);
    setReviewMap({});
  };

  const setReviewComment = (studentId, text) => {
    setReviewMap((m) => ({
      ...m,
      [studentId]: { ...(m[studentId] || {}), comment: text, dirty: true },
    }));
  };

  const saveReviewFor = async (studentId) => {
    if (!reviewHw) return;
    const entry = reviewMap[studentId];
    if (!entry || !entry.dirty) return;
    setReviewMap((m) => ({ ...m, [studentId]: { ...entry, saving: true } }));
    try {
      await api.post(`/homework/${reviewHw.id}/reviews`, {
        student_id: studentId,
        comment: entry.comment || '',
      });
      setReviewMap((m) => ({
        ...m,
        [studentId]: { ...m[studentId], dirty: false, saving: false },
      }));
      toast.success('Comment saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save comment');
      setReviewMap((m) => ({ ...m, [studentId]: { ...m[studentId], saving: false } }));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [hwRes, clsRes, subRes] = await Promise.all([
        homeworkApi.getAll(),
        classesApi.getAll().catch(() => ({ data: { classes: [] } })),
        subjectsApi.getAll().catch(() => ({ data: { subjects: [] } })),
      ]);
      setHomework(hwRes.data.homework || []);
      setClasses(clsRes.data.classes || []);
      setSubjects(subRes.data.subjects || []);
    } catch (e) {
      toast.error('Failed to load homework');
    } finally {
      setLoading(false);
    }
  };

  const classMap = useMemo(() => {
    const m = {};
    classes.forEach((c) => {
      m[c.id] = `${c.name}${c.section ? ' ' + c.section : ''}`;
    });
    return m;
  }, [classes]);

  const subjectMap = useMemo(() => {
    const m = {};
    subjects.forEach((s) => {
      m[s.id] = s.name;
    });
    return m;
  }, [subjects]);

  const filteredHomework = homework.filter((h) =>
    filterClass === 'all' ? true : h.class_id === filterClass
  );

  // For students/parents: keep a cache of their own review per homework
  const [ownReviews, setOwnReviews] = useState({});  // {homework_id: comment}

  useEffect(() => {
    // Only students/parents need to fetch their own reviews
    if (isTeacherOrAdmin || !filteredHomework.length) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      // Fetch in parallel but cap to avoid hammering for huge lists
      const batch = filteredHomework.slice(0, 50);
      const results = await Promise.allSettled(
        batch.map((h) =>
          api.get(`/homework/${h.id}/reviews`).then((r) => ({ id: h.id, reviews: r.data?.reviews || [] }))
        )
      );
      results.forEach((r) => {
        if (r.status === 'fulfilled' && r.value.reviews.length > 0) {
          // For student/parent role the backend already filters to their own student(s)
          updates[r.value.id] = r.value.reviews[0].comment || '';
        }
      });
      if (!cancelled) setOwnReviews((prev) => ({ ...prev, ...updates }));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [filteredHomework.length, isTeacherOrAdmin]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (hw) => {
    setEditing(hw);
    setForm({
      class_id: hw.class_id || '',
      subject_id: hw.subject_id || '',
      title: hw.title || '',
      description: hw.description || '',
      due_date: hw.due_date || '',
    });
    setOpen(true);
  };

  const handleSave = async (publishAfter = false) => {
    if (!form.class_id || !form.title || !form.description || !form.due_date) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      let hwId;
      if (editing) {
        await homeworkApi.update(editing.id, {
          ...form,
          subject_id: form.subject_id || null,
        });
        hwId = editing.id;
        toast.success('Homework updated');
      } else {
        const res = await homeworkApi.create({
          ...form,
          subject_id: form.subject_id || null,
        });
        hwId = res.data.id;
        toast.success('Homework created');
      }

      if (publishAfter && hwId) {
        await handlePublish(hwId);
      }

      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save homework');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (id) => {
    try {
      const res = await homeworkApi.publish(id);
      const { whatsapp_url, chat_invite_url } = res.data || {};
      if (whatsapp_url) {
        // Open WhatsApp with prefilled text — one-click publish flow
        window.open(whatsapp_url, '_blank', 'noopener,noreferrer');
        toast.success('Opening WhatsApp with the homework message');
      }
      if (chat_invite_url) {
        // Also offer the group invite URL as an info toast
        toast.info('Tip: Use your class WhatsApp group link to paste the message');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to publish');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this homework?')) return;
    try {
      await homeworkApi.delete(id);
      toast.success('Homework deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="homework-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Homework</h1>
          <p className="text-muted-foreground mt-1">
            {isTeacherOrAdmin
              ? 'Create homework and publish to class WhatsApp group in one click.'
              : 'All assignments for your class.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={filterClass} onValueChange={setFilterClass}>
            <SelectTrigger className="w-40" data-testid="homework-class-filter">
              <SelectValue placeholder="All classes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classes</SelectItem>
              {classes.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} {c.section}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isTeacherOrAdmin && (
            <Button onClick={openCreate} data-testid="add-homework-button">
              <Plus className="h-4 w-4 mr-2" />
              New Homework
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredHomework.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="py-12 text-center">
            <BookOpenCheck className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No homework yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredHomework.map((hw) => (
            <Card key={hw.id} className="border border-border" data-testid={`homework-card-${hw.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-lg leading-tight">{hw.title}</CardTitle>
                  {hw.published_at ? (
                    <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
                      Published
                    </Badge>
                  ) : (
                    <Badge variant="outline">Draft</Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mt-1">
                  <span className="inline-flex items-center gap-1">
                    <BookOpenCheck className="h-3 w-3" />
                    {classMap[hw.class_id] || hw.class_id}
                  </span>
                  {hw.subject_id && (subjectMap[hw.subject_id] || hw.subject_name) && (
                    <span className="inline-flex items-center gap-1">
                      · {subjectMap[hw.subject_id] || hw.subject_name}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" />
                    Due {hw.due_date}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm whitespace-pre-wrap">{hw.description}</p>
                <p className="text-xs text-muted-foreground">
                  By {hw.created_by_name || 'Teacher'}
                </p>
                {!isTeacherOrAdmin && ownReviews[hw.id] && (
                  <div
                    className="border-l-4 border-primary bg-primary/5 rounded-r p-3 space-y-1"
                    data-testid={`student-review-${hw.id}`}
                  >
                    <p className="text-xs font-semibold text-primary flex items-center gap-1">
                      <MessageSquare className="h-3.5 w-3.5" />
                      Teacher's Comment
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{ownReviews[hw.id]}</p>
                  </div>
                )}
                {isTeacherOrAdmin && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={() => handlePublish(hw.id)}
                      data-testid={`publish-homework-${hw.id}`}
                    >
                      <MessageCircle className="h-4 w-4 mr-1" />
                      Publish to WhatsApp
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openReview(hw)}
                      data-testid={`review-homework-${hw.id}`}
                    >
                      <MessageSquare className="h-4 w-4 mr-1" />
                      Review
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(hw)}
                      data-testid={`edit-homework-${hw.id}`}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(hw.id)}
                      data-testid={`delete-homework-${hw.id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Homework' : 'New Homework'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Class *</Label>
                <Select
                  value={form.class_id}
                  onValueChange={(v) => setForm({ ...form, class_id: v })}
                >
                  <SelectTrigger data-testid="hw-class-select">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} {c.section}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject</Label>
                <Select
                  value={form.subject_id}
                  onValueChange={(v) => setForm({ ...form, subject_id: v })}
                >
                  <SelectTrigger data-testid="hw-subject-select">
                    <SelectValue placeholder="(optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects
                      .filter((s) => !form.class_id || s.class_id === form.class_id)
                      .map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Math Ex 5.2"
                data-testid="hw-title-input"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe the homework clearly"
                rows={5}
                data-testid="hw-description-input"
              />
            </div>
            <div>
              <Label>Due date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start font-normal"
                    data-testid="hw-due-date-input"
                  >
                    <CalendarIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                    {form.due_date
                      ? format(new Date(form.due_date), 'PPP')
                      : <span className="text-muted-foreground">Pick a due date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={form.due_date ? new Date(form.due_date) : undefined}
                    onSelect={(d) => setForm({ ...form, due_date: d ? format(d, 'yyyy-MM-dd') : '' })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              disabled={saving}
              data-testid="save-homework-button"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save draft
            </Button>
            <Button
              onClick={() => handleSave(true)}
              disabled={saving}
              data-testid="publish-homework-submit-button"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Save & Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review (per-student comments) Dialog */}
      <Dialog open={reviewOpen} onOpenChange={(v) => (v ? null : closeReview())}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="homework-review-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Review: {reviewHw?.title}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Add a short comment for each student. Students and parents can read this in
              their portals. Click <b>Save</b> next to a student to record the comment.
            </p>
          </DialogHeader>
          {reviewLoading ? (
            <div className="py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : reviewStudents.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">No students in this class.</p>
          ) : (
            <div className="space-y-3">
              {reviewStudents.map((s) => {
                const entry = reviewMap[s.id] || { comment: '', dirty: false, saving: false };
                return (
                  <div
                    key={s.id}
                    className="border rounded-lg p-3 space-y-2"
                    data-testid={`homework-review-row-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-medium text-sm">{s.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Roll: {s.roll_number || '-'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => saveReviewFor(s.id)}
                        disabled={!entry.dirty || entry.saving}
                        data-testid={`save-review-${s.id}`}
                      >
                        {entry.saving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : entry.dirty ? (
                          'Save'
                        ) : (
                          'Saved'
                        )}
                      </Button>
                    </div>
                    <Textarea
                      value={entry.comment}
                      onChange={(e) => setReviewComment(s.id, e.target.value)}
                      placeholder="e.g. Well done. Try to write neater next time."
                      rows={2}
                      data-testid={`review-comment-${s.id}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeReview}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
